import React, { useCallback, useMemo, useRef } from 'react';
import { Button, Text } from '@fragments';
import { IconAlertTriangle, IconBrandInstagram, IconHistory, IconMessageBolt, IconPhoto } from '@tabler/icons-react';
import ArtEditor, { normalizeDocument } from '../../../../features/ArtEditor';
import StudioAi from '../../../../components/StudioAi';
import SocialAvatar from '../../../../components/SocialAvatar';
import { runLabelKey } from '../RunProgress/hooks/useRunProgress';
import { openImageQuestion } from '../../../../dialogs/ImageChoiceDialog/format';
import memoStudio from '../../../../utils/memoStudio';
import { STUDIO_AUTOMATIONS } from '../../../../enums/routes';
import { automationsLive } from '../../../../enums/automations';
import './styles.scss';

// The places of an open image question, outlined on the draft: all of them, or the one the
// dialog is on (`modal.slot`), whose page comes to the stage.
function questionHighlight(question, slot) {
  const places = question.images || [];
  const focus = places.find(place => place.slot_id === slot) || null;
  const ids = (focus ? [focus] : places).map(place => place.layer_id).filter(Boolean);
  return { ids, key: `${question.id}:${question.version}:${focus?.slot_id || 'all'}`, pageId: (focus || places[0])?.page_id || null, persist: true };
}

// The art of a single-post or carousel project: the editor feature (features/ArtEditor) on
// the Studio's art state (useArt). While a run works the editor is locked under its
// progress; the version picker (top of the editor's rail) views older revisions in place;
// its close sits at the top of the right rail; a carousel's pages sit at the rail's foot,
// new ones coming from the template gallery; publishing and DM automation are listed as
// "soon" (ClickUp 868mf2t0w). While the run asks for images (contract 2.9.0) the editor
// shows the run's draft (`action.preview`) read-only, with the places outlined.
function ArtPanel({ studio: s, visible, onClose, onAskAI }) {
  const { t, art, current, run, setReply } = s;
  const question = openImageQuestion(run);
  const draftSource = question?.preview?.document || null;
  const draft = useMemo(() => (draftSource ? normalizeDocument(draftSource) : null), [draftSource]);
  const slot = s.modal?.type === 'images' ? s.modal.slot : null;
  const highlight = useMemo(() => (draft ? questionHighlight(question, slot) : null), [draft, question, slot]);
  const working = !!run && ['queued', 'running'].includes(run.status);
  // Once answered, the draft stays under the progress while the same run places the images.
  const held = useRef({ runId: null, document: null });
  if (draft) held.current = { runId: run.id, document: draft };
  const waiting = !draft && working && held.current.runId === run.id ? held.current.document : null;
  const busy = s.active && !draft ? { label: t(runLabelKey(run, true)), mark: <StudioAi size={28} active={working} /> } : null;
  const shown = art.viewed || art.latest;
  const versions = shown && !draft && !waiting ? { label: t('revision', { number: shown.revision_number }), items: art.revisions.slice().reverse().map(revision => ({
    label: t('revision', { number: revision.revision_number }), active: revision.id === shown.id,
    // A version is an AI result; "edited" marks one the creator changed afterwards.
    shortcut: [revision.id === art.latest?.id && t('latestVersion'), revision.edited && t('artEdited')].filter(Boolean).join(' · ') || undefined,
    onClick: () => (revision.id === art.latest?.id ? art.backToLatest() : art.view(revision.id)),
  })) } : null;
  const ratio = art.document?.aspect_ratio || current.brief.art?.aspect_ratio || '4:5';
  // What the AI flagged about its composition (a headline longer than its slot…), until edited.
  const warning = !art.viewed && !art.dirty && art.latest?.warnings?.[0];
  const notice = draft ? <div className="studio-art__viewing" role="status">
    <Text.caption contrast={7} left={<IconPhoto />}>{t('imagesDraftNotice')}</Text.caption>
    <Button size="small" color="inverse" variant="rounded" preventLoading onClick={() => s.setModal({ type: 'images', ...(slot ? { slot } : {}) })}>{t('imagesChoose')}</Button>
  </div> : warning ? <div className="studio-art__viewing is-warning" role="status">
    <Text.caption contrast={8} left={<IconAlertTriangle />}>{warning.message}</Text.caption>
  </div> : art.viewed ? <div className="studio-art__viewing" role="status">
    <Text.caption contrast={7} left={<IconHistory />}>{t('viewingVersion', { number: art.viewed.revision_number })}</Text.caption>
    <div className="studio-row">
      <Button size="small" color="overlay" variant="rounded" preventLoading disabled={!!busy} onClick={art.restoreViewed}>{t('artRestoreVersion')}</Button>
      <Button size="small" color="inverse" variant="rounded" preventLoading onClick={art.backToLatest}>{t('backToLatest')}</Button>
    </div>
  </div> : null;
  const actions = [
    automationsLive ? { id: 'automation', label: t('artActionAutomation'), icon: IconMessageBolt, tooltip: t('dm'), onClick: () => s.navigate(STUDIO_AUTOMATIONS) }
      : { id: 'automation', label: t('artActionAutomation'), icon: IconMessageBolt, disabled: true, badge: t('soon'), tooltip: `${t('dm')} · ${t('soon')}` },
    { id: 'publish', label: t('artActionPublish'), icon: IconBrandInstagram, disabled: true, badge: t('soon'), tooltip: `${t('publish')} · ${t('soon')}` },
  ];
  const profile = s.profile ? { name: s.profile.name, handle: s.profile.handle,
    avatar: <SocialAvatar picture={s.profile.picture} label={s.profile.handle} platform={s.profile.platform} className="studio-art__avatar" /> } : null;
  // "Edit with AI" scopes the chat to the layers; the photo panel's "Gerar com IA" also
  // leaves a prompt in the field for the creator to finish.
  const { askAI } = art;
  const askChat = useCallback((layers, ask) => {
    askAI(layers);
    if (ask?.prompt) setReply(ask.prompt);
    onAskAI?.();
  }, [askAI, setReply, onAskAI]);
  return <section className={`studio-art ${visible ? 'is-visible' : ''}`} aria-label={t('art')}>
    <ArtEditor value={art.document} onChange={art.edit} preview={draft || waiting || art.viewedDocument} busy={busy} readOnly={!art.document}
      placeholderRatio={ratio} title={current.title} status={art.status ? { state: art.status, onRetry: art.save } : null}
      conflict={art.conflict ? { onKeep: art.keepMine, onLoad: art.loadSaved } : null} notice={notice} highlight={highlight || art.highlight}
      brandKit={art.brandKit} onSaveBrandKit={art.brandKit ? art.saveBrandKit : null} images={art.images} videos={art.videos} gallery={art.gallery} profile={profile}
      caption={draft ? question.preview.caption : art.viewed ? art.viewed.caption : art.caption} onCaptionChange={art.editCaption} actions={actions}
      versions={versions} close={onClose ? { label: t('closeArt'), onClick: onClose } : null} panelPlacement="outside" onAskAI={askChat}
      imageGeneration={!!s.features?.art_image_generation} beforeExport={art.savedDocument} onExported={draft || waiting ? null : art.onExported} />
  </section>;
}

// Every `studio` field the panel reads (`modal`: the place the image dialog is on;
// `features`: whether the editor offers "Gerar com IA").
export default memoStudio(ArtPanel, ['t', 'art', 'current', 'run', 'active', 'profile', 'modal', 'setModal', 'setReply', 'features', 'navigate']);
